// src/pages/Vit.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { generateVitPdfBlob } from '../pdf/clientPdf';
import { salvaOffline, senzaRete, TIPI, MESSAGGIO_OFFLINE } from '../offline/salvaOffline';
import { Card } from '../components/ui';
import DatiTrasferta, { CAMPI_TRASFERTA } from '../components/DatiTrasferta';

const API_BASE = '/api';    // -> /api/api/work/...
const GAS_API_BASE = '/api';

const templates = [
  { id: 'CIESSE_IMPIANTI', label: 'Ciesse Impianti', file: '/VIT/CiesseImpianti.pdf' },
  { id: 'CIESSE_SERVICE', label: 'Ciesse Service', file: '/VIT/CiesseService.pdf' },
];

const formatEuro = (val) => {
  if (val === '' || val === null || val === undefined) return '';
  const normalized = String(val).replace(',', '.');
  const num = parseFloat(normalized);
  if (!Number.isFinite(num)) return '';
  return num.toFixed(2).replace('.', ',');
};

function getCleanUserName(user) {
  if (!user) return '';
  const candidates = [];
  if (user.username) candidates.push(user.username);
  if (user.nome || user.cognome) candidates.push(`${user.nome || ''} ${user.cognome || ''}`.trim());
  if (user.fullName) candidates.push(user.fullName);

  let name = candidates.find(Boolean) || '';
  name = name
    .split(/\s+/)
    .filter((part) => !part.includes('@'))
    .join(' ')
    .trim();

  if (name) return name;

  if (user.email && user.email.includes('@')) {
    const local = user.email.split('@')[0];
    return local.replace('.', ' ').trim();
  }
  return '';
}

function formatNow() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} alle ore ${hh}:${min}`;
}

const isoToIt = (iso) => {
  const s = String(iso || '').trim();
  if (!s) return '';
  // già IT
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  // ISO YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('it-IT');
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('it-IT');
  return s;
};

export default function Vit() {
  const navigate = useNavigate();

  const [selectedTemplate, setSelectedTemplate] = useState('CIESSE_IMPIANTI');

  // progressivo
  const [workOrderId, setWorkOrderId] = useState(null);
  const [internalCode, setInternalCode] = useState('');

  // Dati VIT (NO "tecnico")
  const [formData, setFormData] = useState({
    ...CAMPI_TRASFERTA,
    data: '', // ISO dal date picker
    cliente: '',
    impianto: '',
    luogo: '',
    telefono: '',
    numeroIntervento: '',
    modello: '',
    matricolaImpianto: '',
    inventario: '',

    // Note interne (NON sul PDF)
    noteGenerali: '',

    // Box costi
    dirittoFissoQuantita: '1',
    dirittoFissoEuro: '',
    oreLavoroQuantita: '',
    oreLavoroEuro: '',

    ricambi: Array.from({ length: 8 }, () => ({ quantita: '', descrizione: '', euro: '' })),

    lavoriEseguiti: '',
  });

  // firme
  const [tecnicoSignature, setTecnicoSignature] = useState(null);
  const [clienteSignature, setClienteSignature] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // GAS
  const [gasUsedName, setGasUsedName] = useState('');
  const [gasUsedAmount, setGasUsedAmount] = useState('');
  const [gasRecoveredName, setGasRecoveredName] = useState('');
  const [gasRecoveredAmount, setGasRecoveredAmount] = useState('');
  const [availableGases, setAvailableGases] = useState([]);

  // ===== FIRMA (come Manutenzione.jsx) =====
  const [signModal, setSignModal] = useState({ open: false, type: null });
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });

  const pdfUrl = useMemo(
    () => templates.find((t) => t.id === selectedTemplate)?.file || '',
    [selectedTemplate]
  );

  // blocco scroll con modal aperta
  useEffect(() => {
    if (signModal.open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
    return undefined;
  }, [signModal.open]);

  // carica gas del tecnico
  useEffect(() => {
    const loadMyGases = async () => {
      try {
        const meRes = await authFetch(`${GAS_API_BASE}/user`);
        if (!meRes.ok) return;
        const me = await meRes.json();
        const currentUserId = me.id || me._id;

        const gasRes = await authFetch(`${GAS_API_BASE}/gas`);
        if (!gasRes.ok) return;
        const gasData = await gasRes.json();

        const myGasNames = gasData
          .filter((g) => {
            const uid = g.user?._id || g.user?.id;
            return uid === currentUserId;
          })
          .map((g) => g.gasName);

        const uniqueNames = [...new Set(myGasNames)].sort();
        setAvailableGases(uniqueNames);
      } catch (err) {
        console.error('Errore caricamento gas (VIT):', err);
      }
    };

    loadMyGases();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuantityChange = (fieldName) => (e) => {
    const raw = e.target.value || '';
    const filtered = raw.replace(/\D/g, '');
    setFormData((prev) => ({ ...prev, [fieldName]: filtered }));
  };

  const handleEuroChange = (fieldName) => (e) => {
    const raw = e.target.value || '';
    const cleaned = raw.replace(/[^\d,.\s]/g, '').trim();
    setFormData((prev) => ({ ...prev, [fieldName]: cleaned }));
  };

  const handleRicambioChange = (idx, key) => (e) => {
    const raw = e.target.value || '';
    setFormData((prev) => {
      const next = [...prev.ricambi];
      next[idx] = { ...next[idx], [key]: raw };
      return { ...prev, ricambi: next };
    });
  };

  // ====== FIRMA: setup + scaling + pointer capture ======
  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const getCanvasPoint = (evt) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const clientX = evt.clientX ?? (evt.touches ? evt.touches[0].clientX : 0);
    const clientY = evt.clientY ?? (evt.touches ? evt.touches[0].clientY : 0);

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const onPointerDown = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();

    canvas.setPointerCapture?.(e.pointerId);

    isDrawingRef.current = true;
    const p = getCanvasPoint(e);
    lastPointRef.current = p;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const onPointerMove = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const p = getCanvasPoint(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
  };

  const onPointerUp = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    isDrawingRef.current = false;
    canvas.releasePointerCapture?.(e.pointerId);
  };

  const openSignatureModal = (type) => {
    setSignModal({ open: true, type });
    setTimeout(() => setupCanvas(), 0);
  };

  const clearSignatureCanvas = () => setupCanvas();

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');

    if (signModal.type === 'tecnico') setTecnicoSignature(dataUrl);
    if (signModal.type === 'cliente') setClienteSignature(dataUrl);

    setSignModal({ open: false, type: null });
  };

  const clearSignaturePreview = (type) => {
    if (type === 'tecnico') setTecnicoSignature(null);
    if (type === 'cliente') setClienteSignature(null);
  };

  // ===== GAS: scala/incrementa + notifica =====
  const consumeGasAndNotify = async () => {
    try {
      const meRes = await authFetch(`${GAS_API_BASE}/user`);
      if (!meRes.ok) return;
      const me = await meRes.json();
      const currentUserId = me.id || me._id;

      const messageParts = [];

      // utilizzato
      if (gasUsedName && Number(gasUsedAmount) > 0) {
        const gasRes = await authFetch(`${GAS_API_BASE}/gas`);
        if (gasRes.ok) {
          const gasData = await gasRes.json();
          const gasItem = gasData.find((g) => {
            const uid = g.user?._id || g.user?.id;
            return uid === currentUserId && g.gasName === gasUsedName;
          });

          if (gasItem) {
            const newQty = Math.max(0, Number(gasItem.quantityGrams || 0) - Number(gasUsedAmount));
            const updateRes = await authFetch(`${GAS_API_BASE}/gas/${gasItem._id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ quantityGrams: newQty }),
            });

            if (updateRes.ok) messageParts.push(`ha utilizzato ${gasUsedAmount} g di ${gasUsedName}`);
          }
        }
      }

      // recuperato
      if (gasRecoveredName && Number(gasRecoveredAmount) > 0) {
        const gasRes = await authFetch(`${GAS_API_BASE}/gas`);
        if (gasRes.ok) {
          const gasData = await gasRes.json();
          const gasItem = gasData.find((g) => {
            const uid = g.user?._id || g.user?.id;
            return uid === currentUserId && g.gasName === gasRecoveredName;
          });

          if (gasItem) {
            const newQty = Math.max(0, Number(gasItem.quantityGrams || 0) + Number(gasRecoveredAmount));
            const updateRes = await authFetch(`${GAS_API_BASE}/gas/${gasItem._id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ quantityGrams: newQty }),
            });

            if (updateRes.ok) messageParts.push(`ha recuperato ${gasRecoveredAmount} g di ${gasRecoveredName}`);
          }
        }
      }

      if (messageParts.length > 0) {
        const when = formatNow();
        const displayName = getCleanUserName(me);
        const message = displayName
          ? `${displayName} ${messageParts.join(' e ')} durante un VIT il ${when}.`
          : `${messageParts.join(' e ')} durante un VIT il ${when}.`;

        await authFetch(`${GAS_API_BASE}/notifications`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'gas_usage',
            message,
            roles: ['admin', 'gestione', 'segreteria'],
          }),
        });
      }
    } catch (err) {
      console.error('Errore consumo/recupero gas (VIT):', err);
    }
  };

  // ===== riserva WorkOrder (progressivo) =====
  const ensureWorkOrder = async () => {
    if (workOrderId && internalCode) return { workOrderId, internalCode };

    const res = await authFetch(`${API_BASE}/work/vit/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...formData,
        data: isoToIt(formData.data),
      }),
    });

    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.error || 'Errore creazione VIT (init).');

    const wo = data?.workOrder;
    setWorkOrderId(wo?._id || null);
    setInternalCode(wo?.internalCode || '');
    return { workOrderId: wo?._id, internalCode: wo?.internalCode || '' };
  };

  // ===== genera pdf + chiude lavorazione =====
  const handleGeneratePdf = async () => {
    setError('');
    setSuccessMessage('');

    if (!formData.data) return setError('Seleziona la data intervento.');
    if (!String(formData.cliente || '').trim()) return setError('Compila "Cliente".');
    if (!String(formData.luogo || '').trim()) return setError('Compila "Indirizzo".');
    if (!String(formData.modello || '').trim()) return setError('Compila "Modello".');
    if (!String(formData.matricolaImpianto || '').trim()) return setError('Compila "Matricola".');
    if (!String(formData.inventario || '').trim()) return setError('Compila "Inventario".');

    setLoading(true);

    // SENZA RETE: il verbale si genera lo stesso e resta sul dispositivo.
    if (senzaRete()) {
      try {
        await salvaOffline({
          tipo: TIPI.VIT,
          template: selectedTemplate,
          formData: { ...formData, data: isoToIt(formData.data), noteGenerali: '' },
          tecnicoSignature,
          clienteSignature,
          datiIniziali: { ...formData, data: isoToIt(formData.data) },
          gas: { gasUsedName, gasUsedAmount, gasRecoveredName, gasRecoveredAmount },
          nomeFile: 'verbale_intervento_tecnico_da_sincronizzare.pdf',
        });

        setSuccessMessage(MESSAGGIO_OFFLINE);
      } catch (err) {
        console.error('Errore salvataggio offline VIT:', err);
        setError(err.message || 'Non sono riuscito a salvare il verbale sul dispositivo.');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const { workOrderId: woId, internalCode: code } = await ensureWorkOrder();

      const pdfFormData = {
        ...formData,
        data: isoToIt(formData.data),
        codiceInterno: code,
        noteGenerali: '', // NON sul PDF
      };

      // 1) Salvo SUBITO la lavorazione in corso.
      // Questo crea/aggiorna WorkInProgress, quindi compare in "Lavorazioni in corso".
      const completeRes = await authFetch(`${API_BASE}/work/vit/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workOrderId: woId,
          template: selectedTemplate,
          formData: {
            ...formData,
            data: isoToIt(formData.data),
            codiceInterno: code,
          },
          tecnicoSignature,
          clienteSignature,
        }),
      });

      if (!completeRes.ok) {
        const err = await completeRes.json().catch(() => null);
        throw new Error(err?.error || 'Errore nel salvataggio del VIT in lavorazioni in corso.');
      }

      // 2) Solo dopo genero il PDF.
      const blob = await generateVitPdfBlob({
        template: selectedTemplate,
        formData: pdfFormData,
        tecnicoSignature,
        clienteSignature,
        inviaReport: true,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `verbale_intervento_tecnico_${code || 'SENZA-CODICE'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      await consumeGasAndNotify();

      setSuccessMessage('VIT generato e salvato in lavorazioni in corso correttamente.');
      // navigate('/lavorazioni/terminate');
    } catch (err) {
      console.error('Errore generazione/salvataggio VIT:', err);
      setError(err.message || 'Errore inatteso durante la generazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
              Verbale di Intervento Tecnico (VIT)
            </h1>
            <p className="text-sm text-gray-600">
              Compila il VIT e genera il PDF. Alla conferma, la lavorazione va in “terminate”.
            </p>
          </div>

          <button
            onClick={() => navigate('/')}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Indietro
          </button>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mt-4 rounded-xl border border-green-200 bg-green-50 text-green-700 px-4 py-3 text-sm">
            {successMessage}
          </div>
        )}

        {/* template */}
        <div className="grid sm:grid-cols-2 gap-4 my-6">
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedTemplate(t.id)}
              className={`w-full text-left p-4 rounded-xl border transition shadow-sm ${
                selectedTemplate === t.id
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-blue-400'
              }`}
            >
              <h2 className="font-semibold text-gray-800">{t.label}</h2>
              <p className="text-xs text-gray-500 mt-1">
                Modello PDF: {t.file.replace('/VIT/', '')}
              </p>
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[2.1fr,3fr] gap-8">
          {/* FORM */}
          <div className="space-y-6">
            {/* Dati intervento */}
            <Card className="shadow-sm p-4 overflow-hidden">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Dati intervento</h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Codice interno</label>
                  <input
                    type="text"
                    value={internalCode}
                    readOnly
                    placeholder="Si genera automaticamente"
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Data intervento</label>
                    <input
                      type="date"
                      name="data"
                      value={formData.data}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                      Sul PDF verrà stampata in formato italiano (gg/mm/aaaa).
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Riferimento chiamata</label>
                    <input
                      type="text"
                      name="numeroIntervento"
                      value={formData.numeroIntervento}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Es. ticket / richiesta / ordine"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cliente</label>
                    <input
                      type="text"
                      name="cliente"
                      value={formData.cliente}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Istituto / Presidio</label>
                    <input
                      type="text"
                      name="impianto"
                      value={formData.impianto}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Indirizzo</label>
                    <input
                      type="text"
                      name="luogo"
                      value={formData.luogo}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Telefono</label>
                    <input
                      type="text"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Modello</label>
                    <input
                      type="text"
                      name="modello"
                      value={formData.modello}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Matricola</label>
                    <input
                      type="text"
                      name="matricolaImpianto"
                      value={formData.matricolaImpianto}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Inventario</label>
                    <input
                      type="text"
                      name="inventario"
                      value={formData.inventario}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Note generali (solo interne)</label>
                  <textarea
                    name="noteGenerali"
                    value={formData.noteGenerali}
                    onChange={handleChange}
                    rows={2}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Queste note NON finiscono sul PDF"
                  />
                </div>
              </div>
            </Card>

            {/* GAS */}
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Gas utilizzato / recuperato (solo tracciamento interno)
              </h2>
              <p className="text-sm text-gray-500">
                Non viene scritto sul PDF. Serve solo per aggiornare i livelli gas e inviare notifica interna.
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Gas utilizzato</h3>
                  <div className="space-y-2">
                    <select
                      value={gasUsedName}
                      onChange={(e) => setGasUsedName(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                    >
                      <option value="">Nessun gas utilizzato</option>
                      {availableGases.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={gasUsedAmount}
                      onChange={(e) => setGasUsedAmount(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Quantità (g)"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Gas recuperato</h3>
                  <div className="space-y-2">
                    <select
                      value={gasRecoveredName}
                      onChange={(e) => setGasRecoveredName(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                    >
                      <option value="">Nessun gas recuperato</option>
                      {availableGases.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={gasRecoveredAmount}
                      onChange={(e) => setGasRecoveredAmount(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Quantità (g)"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Costi */}
            <Card className="shadow-sm p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Costi</h3>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Diritto fisso</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600">Quantità</label>
                      <input
                        value={formData.dirittoFissoQuantita}
                        onChange={handleQuantityChange('dirittoFissoQuantita')}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600">Euro</label>
                      <input
                        value={formData.dirittoFissoEuro}
                        onChange={handleEuroChange('dirittoFissoEuro')}
                        onBlur={() =>
                          setFormData((p) => ({
                            ...p,
                            dirittoFissoEuro: formatEuro(p.dirittoFissoEuro),
                          }))
                        }
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-3">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Ore lavoro</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600">Quantità</label>
                      <input
                        value={formData.oreLavoroQuantita}
                        onChange={handleQuantityChange('oreLavoroQuantita')}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600">Euro</label>
                      <input
                        value={formData.oreLavoroEuro}
                        onChange={handleEuroChange('oreLavoroEuro')}
                        onBlur={() =>
                          setFormData((p) => ({
                            ...p,
                            oreLavoroEuro: formatEuro(p.oreLavoroEuro),
                          }))
                        }
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <div className="text-sm font-semibold text-gray-700 mb-2">Ricambi</div>      
                <div className="overflow-x-auto">
                  <div className="min-w-[320px] space-y-2 text-xs md:text-sm">
                    <div className="grid grid-cols-[4rem,minmax(0,1fr),5.5rem] gap-2 font-medium text-gray-700">
                      <span className="text-center">Qtà</span>
                      <span className="text-center">Descrizione pezzo</span>
                      <span className="text-right">Euro</span>
                    </div>
                                    
                    {formData.ricambi.map((r, idx) => (
                      <div key={idx} className="grid grid-cols-[4rem,minmax(0,1fr),5.5rem] gap-2">
                        {/* Quantità: solo numeri, max 4 */}
                        <input
                          type="text"
                          value={r.quantita}
                          maxLength={4}
                          inputMode="numeric"
                          onChange={(e) => {
                            const onlyDigits = (e.target.value || '').replace(/\D/g, '');
                            setFormData((p) => {
                              const next = [...p.ricambi];
                              next[idx] = { ...next[idx], quantita: onlyDigits };
                              return { ...p, ricambi: next };
                            });
                          }}
                          className="border border-gray-300 rounded-lg px-2 py-1 text-xs md:text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="1"
                        />
              
                        {/* Descrizione */}
                        <input
                          type="text"
                          value={r.descrizione}
                          onChange={handleRicambioChange(idx, 'descrizione')}
                          className="border border-gray-300 rounded-lg px-2 py-1 text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Descrizione pezzo"
                        />
              
                        {/* Euro */}
                        <input
                          type="text"
                          value={r.euro}
                          onChange={handleRicambioChange(idx, 'euro')}
                          onBlur={() => {
                            setFormData((p) => {
                              const next = [...p.ricambi];
                              next[idx] = { ...next[idx], euro: formatEuro(next[idx].euro) };
                              return { ...p, ricambi: next };
                            });
                          }}
                          className="border border-gray-300 rounded-lg px-2 py-1 text-xs md:text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0,00"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Lavori eseguiti */}
            <Card className="shadow-sm p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Lavori eseguiti</h3>
              <textarea
                name="lavoriEseguiti"
                value={formData.lavoriEseguiti}
                onChange={handleChange}
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Descrizione intervento / test / esiti / materiali..."
              />
            </Card>

            <DatiTrasferta formData={formData} onChange={handleChange} />

            {/* FIRME (come Manutenzione: preview + bottoni) */}
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <h3 className="font-semibold text-gray-800">Firme</h3>
                  <p className="text-xs text-gray-500">Firma con mouse o dito. (Salvate digitalmente)</p>
                </div>

                <button
                  type="button"
                  onClick={handleGeneratePdf}
                  disabled={loading}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm ${
                    loading ? 'bg-green-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {loading ? 'Generazione...' : 'Genera PDF'}
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                {/* tecnico */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">Firma tecnico</p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openSignatureModal('tecnico')}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {tecnicoSignature ? 'Modifica' : 'Aggiungi'}
                      </button>
                      {tecnicoSignature && (
                        <button
                          type="button"
                          onClick={() => clearSignaturePreview('tecnico')}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancella
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="border rounded-lg bg-gray-50 h-28 flex items-center justify-center overflow-hidden">
                    {tecnicoSignature ? (
                      <img src={tecnicoSignature} alt="Firma tecnico" className="h-full object-contain" />
                    ) : (
                      <span className="text-xs text-gray-400">Nessuna firma</span>
                    )}
                  </div>
                </div>

                {/* cliente */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">Firma cliente</p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openSignatureModal('cliente')}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {clienteSignature ? 'Modifica' : 'Aggiungi'}
                      </button>
                      {clienteSignature && (
                        <button
                          type="button"
                          onClick={() => clearSignaturePreview('cliente')}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancella
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="border rounded-lg bg-gray-50 h-28 flex items-center justify-center overflow-hidden">
                    {clienteSignature ? (
                      <img src={clienteSignature} alt="Firma cliente" className="h-full object-contain" />
                    ) : (
                      <span className="text-xs text-gray-400">Nessuna firma</span>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* PREVIEW */}
          <div className="space-y-3">
            <Card className="shadow-sm p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Anteprima</h3>
              {pdfUrl ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden h-[760px]">
                  <iframe title="Anteprima PDF VIT" src={pdfUrl} className="w-full h-full" />
                </div>
              ) : (
                <p className="text-sm text-gray-500">Seleziona un modello per visualizzare l’anteprima.</p>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* MODALE FIRMA (come Manutenzione.jsx) */}
      {signModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-800">
                Firma {signModal.type === 'tecnico' ? 'tecnico' : 'cliente'}
              </h3>
              <button
                type="button"
                onClick={() => setSignModal({ open: false, type: null })}
                className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <p className="text-xs text-gray-500 mb-2">
              Firma con mouse o dito. Poi premi “Salva firma”.
            </p>

            <div className="border border-gray-300 rounded-lg bg-gray-50 overflow-hidden">
              <canvas
                ref={canvasRef}
                width={900}
                height={360}
                className="w-full h-[220px]"
                style={{ touchAction: 'none' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onPointerLeave={onPointerUp}
              />
            </div>

            <div className="mt-3 flex justify-between items-center">
              <button
                type="button"
                onClick={clearSignatureCanvas}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                Cancella
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSignModal({ open: false, type: null })}
                  className="px-3 py-2 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={saveSignature}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
                >
                  Salva firma
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
